"use client";
import type { BlockUnionFragment } from "@/generated/client.generated";
import { LocalizedJoi } from "@/utils/localized-joi";
import { Logger } from "@/utils/logger";
import type { ValidationError, ValidationResult } from "joi";
import { equals } from "rambda";
import { type ReactNode, createContext, useEffect, useState } from "react";
import { EMPTY_FORM_DATA, type FormData, type FormStep } from "./types";

interface FormStateContextProps {
  formData: FormData;
  setFormData: (fields: Partial<FormData>) => void;
  steps: Array<FormStep>;
  currentStepIndex: number;
  step: FormStep | null;
  isLastStep: boolean;
  goTo: (index: number) => void;
  next: () => void;
  back: () => void;
  validateStep: (data: unknown) => ValidationResult;
  validateForm: (data: unknown) => ValidationResult;
  validateStepField: (data: unknown, fieldName: string) => ValidationResult;
}

export const FormStateContext = createContext<FormStateContextProps>({
  back: () => Promise.resolve(),
  currentStepIndex: 0,
  formData: EMPTY_FORM_DATA,
  goTo: () => Promise.resolve(),
  isLastStep: true,
  next: () => Promise.resolve(),
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  // biome-ignore lint/correctness/noUnusedFunctionParameters: <explanation>
  setFormData: (fields: Partial<FormData>) => Promise.resolve(),
  step: null,
  steps: [],
  validateForm: (data: unknown) => {
    return {
      error: [] as Array<ValidationError>,
      value: data,
      warning: undefined,
    } as unknown as ValidationResult;
  },
  validateStep: (data: unknown) => {
    return {
      error: [] as Array<ValidationError>,
      value: data,
      warning: undefined,
    } as unknown as ValidationResult;
  },
  // biome-ignore lint/correctness/noUnusedVariables: <explanation>
  // biome-ignore lint/correctness/noUnusedFunctionParameters: <explanation>
  validateStepField: (data: unknown, fieldName: string) => {
    return {
      error: [] as Array<ValidationError>,
      value: data,
      warning: undefined,
    } as unknown as ValidationResult;
  },
});

interface MultiStepFormContextProps {
  blocks?: ReadonlyArray<BlockUnionFragment>;
  data: FormData;
  steps: Array<FormStep>;
  children: ReactNode;
}

export function MultiStepFormContext({ data, steps, children }: MultiStepFormContextProps) {
  const [formData, setFormData] = useState(data);
  const [currentStepIndex, setCurrentStepIndex] = useState(data.currentStepIndex);

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  useEffect(() => {
    return setFormData((prev) => {
      if (!equals(prev, { ...formData, ...data })) {
        return { ...formData, ...data };
      }
      return prev;
    });
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Make sure that the currentStepIndex is updated when data changes
  useEffect(() => {
    setCurrentStepIndex(data.currentStepIndex);
  }, [data, steps]);

  function updateFormData(fields: Partial<FormData>) {
    return setFormData((prev) => {
      return { ...prev, ...fields };
    });
  }

  function next() {
    setCurrentStepIndex((i) => {
      if (i >= steps.length - 1) {
        return i;
      }
      return i + 1;
    });
  }

  function back() {
    setCurrentStepIndex((i) => {
      if (i <= 0) {
        return i;
      }
      return i - 1;
    });
  }

  function goTo(index: number) {
    setCurrentStepIndex(index);
  }

  function validateStepField(data: unknown, fieldName: string) {
    const step = steps[currentStepIndex];

    if (!step.validatorSchema) {
      return LocalizedJoi.object().validate(data);
    }

    try {
      return LocalizedJoi.object({
        otherPayer: LocalizedJoi.boolean(),
        value: step?.validatorSchema.extract(fieldName),
      }).validate({ otherPayer: true, value: data });
    } catch (error) {
      Logger.error(error);

      return LocalizedJoi.object().validate(data);
    }
  }

  function validateStep(data: unknown) {
    const step = steps[currentStepIndex];

    if (!step.validatorSchema) {
      return LocalizedJoi.object().validate(data);
    }

    return step?.validatorSchema.validate(data);
  }

  function validateForm(data: unknown) {
    const schemas = Object.fromEntries(
      steps.map(({ id, ...rest }) => [id, rest.validatorSchema ?? LocalizedJoi.object()]),
    );

    let formSchema = LocalizedJoi.object({});

    for (const key in schemas) {
      formSchema = formSchema.keys({ [key]: schemas[key] });
    }

    return formSchema.unknown(true).validate(data);
  }

  return (
    <FormStateContext.Provider
      value={{
        back,
        currentStepIndex: currentStepIndex,
        formData: formData,
        goTo,
        isLastStep: currentStepIndex === steps.length - 1,
        next,
        setFormData: updateFormData,
        step: steps[currentStepIndex],
        steps: steps,
        validateForm: validateForm,
        validateStep: validateStep,
        validateStepField: validateStepField,
      }}
    >
      {children}
    </FormStateContext.Provider>
  );
}
